import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import ResmoMascot from "../components/ResmoMascot";
import AuthenticitySeal from "../components/AuthenticitySeal";
import { api, formatApiErrorDetail } from "../lib/api";
import { Trash2, ArrowRight, Sparkles, GitCompare, Layers } from "lucide-react";
import OutcomeWidget from "../components/OutcomeWidget";
import StudioPublishPanel from "../components/studio/StudioPublishPanel";
import ReportsMenu from "../components/ReportsMenu";

export default function History() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/analyses");
      setItems(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this analysis forever?")) return;
    try {
      await api.delete(`/analyses/${id}`);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="rm-tag mb-3">Past meals</div>
            <h1 className="font-display text-4xl md:text-5xl uppercase">History</h1>
          </div>
          <Link to="/feed-job" className="rm-btn rm-btn-primary" data-testid="history-new-btn">
            <Sparkles size={16} /> Tailor a new one
          </Link>
        </div>

        <ReportsMenu className="mb-6" />

        <div className="mb-8" data-testid="history-publish-wrap" style={{ marginTop: "-0.75rem" }}>
          <StudioPublishPanel collapsible />
        </div>

        {error && (
          <div className="p-3 mb-6 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 font-mono-rm">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16" data-testid="history-empty">
            <ResmoMascot state="hungry" size={180} />
            <div className="font-display text-2xl uppercase mt-4">No meals yet.</div>
            <p className="font-mono-rm text-sm mt-2" style={{ color: "#475569" }}>
              Feed me a job description to get started.
            </p>
            <Link to="/feed-job" className="rm-btn rm-btn-primary mt-6 inline-flex">
              Feed me <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="history-grid">
            {items.map((it) => {
              const score = it.overall_score || 0;
              const c = score >= 75 ? "#10B981" : score >= 50 ? "#FFC145" : "#EF4444";
              const isRetailor = !!it.based_on;
              const parent = isRetailor ? items.find((p) => p.id === it.based_on) : null;
              return (
                <div key={it.id} className="rm-card p-6" data-testid={`history-card-${it.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {isRetailor && (
                        parent ? (
                          <Link
                            to={`/dashboard/${parent.id}`}
                            className="rm-tag mb-2 hover:opacity-80 transition-opacity"
                            style={{ background: "#FEF3C7", borderColor: "#0F1B2D" }}
                            data-testid={`retailor-badge-${it.id}`}
                            title="Open original analysis"
                          >
                            <Layers size={12} /> Re-tailored from {parent.job_title || "earlier version"}
                          </Link>
                        ) : (
                          <div
                            className="rm-tag mb-2"
                            style={{ background: "#FEF3C7", borderColor: "#0F1B2D" }}
                            data-testid={`retailor-badge-${it.id}`}
                          >
                            <Layers size={12} /> Re-tailored
                          </div>
                        )
                      )}
                      <div className="font-display text-xl uppercase leading-tight truncate">
                        {it.job_title || "Untitled role"}
                      </div>
                      <div className="font-mono-rm text-xs uppercase tracking-widest mt-1" style={{ color: "#475569" }}>
                        {it.company_name || "Unknown"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Authenticity seal renders inline left of the fit-score
                          square when this resume's Integrity Check is 100/100
                          with every flag resolved. The clean alpha-transparent
                          PNG (corner whites flood-filled) sits naturally on the
                          card background. */}
                      {it.integrity_verified && (
                        <AuthenticitySeal
                          verified
                          size={56}
                          title="Resmo Certified · This resume passed the Honesty Check at 100/100."
                          testId={`integrity-seal-history-${it.id}`}
                        />
                      )}
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center font-display text-lg"
                        style={{ background: c, color: "white", border: "3px solid #0F1B2D" }}
                      >
                        {Math.round(score)}
                      </div>
                    </div>
                  </div>

                  {it.tone_hint && (
                    <div className="font-mono-rm text-xs mt-3 italic" style={{ color: "#475569" }}>
                      tone: {it.tone_hint.length > 60 ? it.tone_hint.slice(0, 57) + "..." : it.tone_hint}
                    </div>
                  )}

                  <div className="font-mono-rm text-xs mt-3" style={{ color: "#475569" }}>
                    {it.created_at ? new Date(it.created_at).toLocaleString() : ""}
                  </div>

                  <div className="mt-3">
                    <OutcomeWidget analysisId={it.id} compact />
                  </div>

                  <div className="flex gap-2 mt-5 flex-wrap">
                    <Link to={`/dashboard/${it.id}`} className="rm-btn rm-btn-primary text-xs px-3 py-2 flex-1" data-testid={`view-${it.id}`}>
                      Open
                    </Link>
                    {isRetailor && parent && (
                      <Link
                        to={`/compare/${it.based_on}/${it.id}`}
                        className="rm-btn rm-btn-secondary text-xs px-3 py-2"
                        data-testid={`compare-${it.id}`}
                        title="Compare with original"
                      >
                        <GitCompare size={14} />
                      </Link>
                    )}
                    <button onClick={() => handleDelete(it.id)} className="rm-btn rm-btn-ghost text-xs px-3 py-2" data-testid={`delete-${it.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
